import dayjs from 'dayjs'
import { computed, ref } from 'vue'
import {
  fetchRateHistory,
  fetchRateAuditTrail as fetchRateAuditTrailApi,
  createRate as createRateApi,
} from '@/services/rates'
import type { Rate, CreateRateRequest, RateAuditTrailEntry } from '@/services/rates'

const rates = ref<Rate[]>([])
const isFetchingRates = ref(false)
const fetchRatesError = ref<Error>()

// A rate valid from today counts as current, not upcoming.
const isUpcomingRate = (rate: Rate) => dayjs(rate.validFrom).isAfter(dayjs(), 'day')
const currentAndHistoricRates = computed(() => rates.value.filter((rate) => !isUpcomingRate(rate)))
const upcomingRates = computed(() => rates.value.filter(isUpcomingRate))

const rateAuditTrail = ref<RateAuditTrailEntry[]>([])
const isFetchingRateAuditTrail = ref(false)
const fetchRateAuditTrailError = ref<Error>()

const isCreatingRate = ref(false)
const createRateError = ref<Error>()

const toError = (err: unknown, fallbackMessage: string) => (err instanceof Error ? err : new Error(fallbackMessage))

export const usePricing = () => {
  const fetchRates = async () => {
    try {
      isFetchingRates.value = true
      fetchRatesError.value = undefined
      rates.value = await fetchRateHistory()
    } catch (err) {
      fetchRatesError.value = toError(err, 'Failed to fetch rates')
    } finally {
      isFetchingRates.value = false
    }
  }

  const fetchAuditTrail = async () => {
    try {
      isFetchingRateAuditTrail.value = true
      fetchRateAuditTrailError.value = undefined
      rateAuditTrail.value = await fetchRateAuditTrailApi()
    } catch (err) {
      fetchRateAuditTrailError.value = toError(err, 'Failed to fetch audit trail')
    } finally {
      isFetchingRateAuditTrail.value = false
    }
  }

  const createRate = async (payload: CreateRateRequest) => {
    try {
      isCreatingRate.value = true
      createRateError.value = undefined
      await createRateApi(payload)
    } catch (err) {
      const error = toError(err, 'Failed to create rate')
      createRateError.value = error
      throw error
    } finally {
      isCreatingRate.value = false
    }
  }

  return {
    currentAndHistoricRates,
    upcomingRates,
    fetchRates,
    isFetchingRates,
    fetchRatesError,

    rateAuditTrail,
    fetchAuditTrail,
    isFetchingRateAuditTrail,
    fetchRateAuditTrailError,

    createRate,
    isCreatingRate,
    createRateError,
  }
}
